import Head from "next/head";
import { useRouter } from "next/router";
import { useFormatMessage } from "components/base/Trans";

const SITE_BASE_URL = "https://www.hillhelicopters.com";
const TITLE_DIVIDER = " - ";
const MAX_SEO_TITLE_LENGTH = 60;

const HREFLANG_MAP = {
  en: "en",
  ru: "ru",
  "es-la": "es-419",
  "pt-br": "pt-BR",
  it: "it-IT",
  fr: "fr-FR",
  de: "de-DE",
};

const buildSeoTitle = (title, keywords, formatMessage) => {
  const siteName = formatMessage({ id: "LBL_META_TITLE" });

  if (!title) return siteName;

  const translatedTitle = formatMessage({ id: title });
  const baseTitle = translatedTitle.concat(TITLE_DIVIDER, siteName);

  if (baseTitle.length > MAX_SEO_TITLE_LENGTH || !keywords?.length) {
    return baseTitle;
  }

  for (const keyword of keywords) {
    if (!keyword) continue;

    const titleWithKeyword = translatedTitle.concat(
      TITLE_DIVIDER,
      formatMessage({ id: keyword }),
      TITLE_DIVIDER,
      siteName,
    );

    if (titleWithKeyword.length <= MAX_SEO_TITLE_LENGTH) {
      return titleWithKeyword;
    }
  }

  return baseTitle;
};

const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  return imageUrl.startsWith("https")
    ? imageUrl
    : `${SITE_BASE_URL}${imageUrl}`;
};
const OG_LOCALE_MAP = {
  en: "en_US",
  ru: "ru_RU",
  "es-la": "es_LA",
  "pt-br": "pt_BR",
  it: "it_IT",
  fr: "fr_FR",
  de: "de_DE",
};

const MetaHead = ({
  title,
  keywords,
  description,
  imageUrl,
  imageDimensions,
  structuredData,
  ogType,
  children,
}) => {
  const router = useRouter();
  const formatMessage = useFormatMessage();
  const metaTitle = buildSeoTitle(title, keywords, formatMessage);
  const metaDescription = description
    ? formatMessage({ id: description })
    : null;
  const resolvedImageUrl = resolveImageUrl(imageUrl);
  const resolvedImageDimensions = imageDimensions || {
    width: "1200",
    height: "630",
  };

  const canonicalPath = router.asPath.split("?")[0];
  const pathWithoutLocale =
    router.locale && router.locale !== router.defaultLocale
      ? canonicalPath.replace(`/${router.locale}`, "")
      : canonicalPath;

  const ogLocale = OG_LOCALE_MAP[router.locale] || "en_US";

  return (
    <Head>
      <title key="title">{metaTitle}</title>
      <meta property="og:title" key="og-title" content={metaTitle} />
      <meta name="twitter:title" key="twitter-title" content={metaTitle} />
      <meta
        property="og:site_name"
        key="og-site-name"
        content="Hill Helicopters"
      />
      <meta property="og:type" key="og-type" content={ogType || "website"} />
      <meta property="og:locale" key="og-locale" content={ogLocale} />
      <meta
        property="og:url"
        key="og-url"
        content={`${SITE_BASE_URL}${canonicalPath}`}
      />
      <link
        rel="canonical"
        key="canonical"
        href={`${SITE_BASE_URL}${canonicalPath}`}
      />

      {router.locales &&
        router.locales.map((locale) => {
          const hreflang = HREFLANG_MAP[locale] || locale;
          const localePrefix =
            locale === router.defaultLocale ? "" : `/${locale}`;
          return (
            <link
              key={`hreflang-${locale}`}
              rel="alternate"
              hrefLang={hreflang}
              href={`${SITE_BASE_URL}${localePrefix}${pathWithoutLocale}`}
            />
          );
        })}
      <link
        rel="alternate"
        hrefLang="x-default"
        key="hreflang-x-default"
        href={`${SITE_BASE_URL}${pathWithoutLocale}`}
      />

      {metaDescription && (
        <>
          <meta
            property="og:description"
            key="og-description"
            content={metaDescription}
          />
          <meta
            name="description"
            key="description"
            content={metaDescription}
          />
          <meta
            name="twitter:description"
            key="twitter-description"
            content={metaDescription}
          />
        </>
      )}

      {resolvedImageUrl && (
        <>
          <meta
            property="twitter:image"
            key="twitter-image"
            content={resolvedImageUrl}
          />
          <meta
            name="image"
            property="og:image"
            key="og-image"
            content={resolvedImageUrl}
          />
          {resolvedImageDimensions && (
            <>
              <meta
                property="og:image:width"
                key="og-image-width"
                content={resolvedImageDimensions.width}
              />
              <meta
                property="og:image:height"
                key="og-image-height"
                content={resolvedImageDimensions.height}
              />
            </>
          )}
        </>
      )}

      {structuredData && (
        <script type="application/ld+json" key="structured-data">
          {structuredData}
        </script>
      )}

      {children}
    </Head>
  );
};

export default MetaHead;
